import './styles.scss';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { Typography } from 'Components/atoms';
import { DropdownComponent } from 'Components/molecules';
import { RiArrowLeftLine } from '@react-icons/all-files/ri/RiArrowLeftLine';
import { BsLightbulb } from '@react-icons/all-files/bs/BsLightbulb';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import { useSelector } from 'react-redux';
import { RootState } from 'Rdx/store';
import { ToastsContext } from 'Rdx/contexts/ToastsContext';
import { createRule, updateRule, listRuleSets, fetchRuleById } from 'Apis/library';
import { RuleSetDto } from 'Core-utils/types/RuleSetsConfig.types';
import {
    DEFAULT_ERROR_MSG,
    RULE_BUILDER_CREATE_TITLE,
    RULE_BUILDER_EDIT_TITLE,
    RULE_BUILDER_CREATE_SUBTITLE,
    RULE_BUILDER_EDIT_SUBTITLE,
    RULE_BUILDER_SELECT_RULE_SET,
    RULE_BUILDER_CANCEL,
    RULE_BUILDER_SAVE,
    RULE_BUILDER_SAVING,
    RULE_BUILDER_SIMPLE_MODE_TITLE,
    RULE_BUILDER_SIMPLE_MODE_SUBTITLE,
    RULE_BUILDER_ADVANCED_MODE_TITLE,
    RULE_BUILDER_ADVANCED_MODE_SUBTITLE,
    RULE_BUILDER_SIMPLE_TIP,
    RULE_BUILDER_ADVANCED_TIP,
    RULE_BUILDER_RULE_UPDATED,
    RULE_BUILDER_RULE_CREATED,
    RULE_BUILDER_LOADING,
    FIELD_TYPES,
    OPERATORS_WITHOUT_VALUE,
} from 'Core-utils/constants/constants';
import { useI18n } from 'Hooks/useI18n';
import {
    ActionEntry,
    BuilderMode,
    CondEntry,
    ConditionOperator,
    DATETIME_CUSTOM_SENTINEL,
    GroupNode,
    LogicOp,
    RuleAction,
    RuleCondition,
    RuleDto,
} from 'Core-utils/types/rules.types';
import {
    actionsToApi,
    advancedToConditionGroups,
    conditionGroupsToState,
    emptyAction,
    emptyCond,
    emptyGroup,
    nextId,
    simpleToConditionGroups,
} from 'Core-utils/helpers/ruleBuilderHelpers';
import { ModeTab } from 'Components/atoms/ModeTab';
import { AdvancedMode } from 'Components/organisms/AdvancedMode';
import { RuleSidebar } from 'Components/organisms/RuleSidebar';
import { SimpleMode } from 'Components/organisms/SimpleMode';

// ─── RuleBuilderModal ─────────────────────────────────────────────────────────

export interface RuleBuilderModalProps {
    show: boolean;
    onClose: () => void;
    rule: RuleDto | null;
    onSaved?: () => void;
}

const RuleBuilderModal = ({ show, onClose, rule, onSaved }: RuleBuilderModalProps) => {
    const { t } = useI18n();
    const { addToast } = useContext(ToastsContext);
    const auth = useSelector((state: RootState) => state.auth);
    const tid = ((auth as any).selectedTenant?.id as string) ?? '';

    const isEdit = !!rule;

    // ── Core state ────────────────────────────────────────────────────────────
    const [ruleLoading, setRuleLoading] = useState(false);
    // freshRule holds the server-fetched version when in edit mode.
    // While it's being fetched we show a spinner; the prop `rule` is only used
    // to know the ID to fetch — never as the source of truth for form state.
    const [freshRule, setFreshRule] = useState<RuleDto | null>(null);
    const [mode, setMode] = useState<BuilderMode>('simple');
    const [ruleSetId, setRuleSetId] = useState('');
    const [ruleSetSearch, setRuleSetSearch] = useState('');
    const [priority, setPriority] = useState(0);
    const [sidebarHidden, setSidebarHidden] = useState(false);

    // Simple mode
    const [simpleBehavior, setSimpleBehavior] = useState<LogicOp>('and');
    const [simpleConditions, setSimpleConditions] = useState<CondEntry[]>([emptyCond(), emptyCond()]);

    // Advanced mode
    const [advancedChildren, setAdvancedChildren] = useState<Array<CondEntry | GroupNode>>([emptyGroup()]);

    // Shared actions
    const [thenActions, setThenActions] = useState<ActionEntry[]>([emptyAction()]);
    const [elseActions, setElseActions] = useState<ActionEntry[]>([{ ...emptyAction(), actionType: 'SKIP' }]);
    const [saving, setSaving] = useState(false);

    // ── Rule Sets ──────────────────────────────────────────────────────────────
    // Use the same SWR key as RulesConfig so both components share one cache entry.
    const { data: ruleSetsList, isLoading: isRuleSetsLoading } = useApiObjectData<{ data: unknown }, RuleSetDto[]>(
        tid ? `rulesets-${tid}` : '',
        listRuleSets,
        (res) => {
            const body = res?.data;
            if (Array.isArray(body)) {
                return body as RuleSetDto[];
            }
            if (Array.isArray((body as { data?: unknown })?.data)) {
                return (body as { data: RuleSetDto[] }).data;
            }
            return [];
        },
        { tid, limit: 200 },
        !!tid,
    );

    const allRuleSetItems = useMemo(
        () => (Array.isArray(ruleSetsList) ? ruleSetsList : []).map((rs) => ({
            id: rs.id,
            displayValue: rs.name ?? rs.id,
            value: rs.id,
        })),
        [ruleSetsList],
    );

    const filteredRuleSetItems = useMemo(() => {
        if (!ruleSetSearch.trim()) {
            return allRuleSetItems;
        }
        const q = ruleSetSearch.toLowerCase();
        return allRuleSetItems.filter((rs) => rs.displayValue.toLowerCase().includes(q));
    }, [allRuleSetItems, ruleSetSearch]);

    const selectedRuleSetName = useMemo(
        () => allRuleSetItems.find((rs) => rs.value === ruleSetId)?.displayValue ?? ruleSetId,
        [allRuleSetItems, ruleSetId],
    );

    // Sync mode: simple→advanced mirrors conditions into a single group.
    // In edit mode, advancedChildren is already loaded from API with full nested
    // structure — never overwrite it when the user switches to advanced.
    const handleSetMode = useCallback(
        (next: BuilderMode) => {
            if (next === 'advanced' && mode === 'simple') {
                if (!isEdit) {
                    // Create mode only: build advanced tree from flat simple conditions
                    setAdvancedChildren([{ kind: 'group', id: nextId(), logic: simpleBehavior, children: simpleConditions }]);
                }
                // Edit mode: advancedChildren already holds the correctly nested API state
            }
            if (next === 'simple' && mode === 'advanced') {
                const firstGroup = advancedChildren.find((c) => c.kind === 'group') as GroupNode | undefined;
                if (firstGroup) {
                    const flatConds = firstGroup.children.filter((c) => c.kind === 'cond') as CondEntry[];
                    setSimpleConditions(flatConds.length ? flatConds : [emptyCond()]);
                    setSimpleBehavior(firstGroup.logic);
                }
            }
            setMode(next);
        },
        [isEdit, mode, simpleBehavior, simpleConditions, advancedChildren],
    );

    // Populate form state from a resolved RuleDto (either freshly fetched or blank for create).
    const populateFromRule = useCallback((resolved: RuleDto | null) => {
        if (resolved) {
            setRuleSetId(resolved.rule_set_id ?? '');
            setPriority(resolved.priority ?? 1);

            if (resolved.conditionGroups?.length) {
                const stateGroups = conditionGroupsToState(resolved.conditionGroups);
                setAdvancedChildren(stateGroups);
                const first = stateGroups[0];
                if (first) {
                    const flatConds = first.children.filter((c) => c.kind === 'cond') as CondEntry[];
                    setSimpleConditions(flatConds.length ? flatConds : [emptyCond()]);
                    setSimpleBehavior(first.logic);
                }
            } else {
                const loaded: CondEntry[] = (resolved.conditions ?? []).map((c: RuleCondition) => ({
                    kind: 'cond',
                    id: nextId(),
                    fieldPath: c.name,
                    fieldType: c.type,
                    operator: c.operator,
                    value: c.value,
                }));
                setSimpleConditions(loaded.length ? loaded : [emptyCond()]);
                setAdvancedChildren([{ kind: 'group', id: nextId(), logic: 'and', children: loaded.length ? loaded : [emptyCond()] }]);
            }

            const thenApi = (resolved.actions ?? []).filter((a: RuleAction) => a.action_type === 'then');
            const elseApi = (resolved.actions ?? []).filter((a: RuleAction) => a.action_type === 'else');
            setThenActions(thenApi.length ? thenApi.map((a: RuleAction) => ({ id: nextId(), actionType: a.action_data?.type ?? 'ADD_TAG' })) : [emptyAction()]);
            setElseActions(elseApi.length ? elseApi.map((a: RuleAction) => ({ id: nextId(), actionType: a.action_data?.type ?? 'SKIP' })) : [{ ...emptyAction(), actionType: 'SKIP' }]);

            const hasNestedGroups = (resolved.conditionGroups ?? []).some(
                (g) => (g.childGroups?.length ?? 0) > 0,
            );
            setMode(hasNestedGroups ? 'advanced' : 'simple');
        } else {
            setRuleSetId('');
            setRuleSetSearch('');
            setPriority(1);
            setSimpleBehavior('and');
            setSimpleConditions([emptyCond(), emptyCond()]);
            setAdvancedChildren([emptyGroup()]);
            setThenActions([emptyAction()]);
            setElseActions([{ ...emptyAction(), actionType: 'SKIP' }]);
            setMode('simple');
        }
        setSidebarHidden(false);
    }, []);

    useEffect(() => {
        if (!show) {
            // Reset fresh rule when modal closes so next open starts clean.
            setFreshRule(null);
            return;
        }

        if (!rule) {
            // Create mode — no fetch needed.
            populateFromRule(null);
            return;
        }

        // Edit mode — fetch the latest version from the server so we never
        // show stale grid-row data regardless of SWR revalidation timing.
        let cancelled = false;
        setRuleLoading(true);
        fetchRuleById(rule.id)
            .then((res) => {
                if (cancelled) {
                    return;
                }
                const fetched: RuleDto = (res as any)?.data ?? rule;
                setFreshRule(fetched);
                populateFromRule(fetched);
            })
            .catch(() => {
                if (cancelled) {
                    return;
                }
                // Fall back to grid row data if the fetch fails.
                setFreshRule(rule);
                populateFromRule(rule);
            })
            .finally(() => {
                if (!cancelled) {
                    setRuleLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [show, rule?.id, populateFromRule, fetchRuleById]);

    // ── Action helpers ────────────────────────────────────────────────────────
    const updateActions = useCallback(
        (setter: React.Dispatch<React.SetStateAction<ActionEntry[]>>, id: string, patch: Partial<ActionEntry>) => {
            const applyPatch = (a: ActionEntry) => (a.id === id ? { ...a, ...patch } : a);
            setter((prev) => prev.map(applyPatch));
        },
        [],
    );

    const removeAction = useCallback(
        (setter: React.Dispatch<React.SetStateAction<ActionEntry[]>>, id: string) => {
            const notId = (a: ActionEntry) => a.id !== id;
            setter((prev) => prev.filter(notId));
        },
        [],
    );

    const addAction = useCallback((setter: React.Dispatch<React.SetStateAction<ActionEntry[]>>) =>
        setter((prev) => [...prev, emptyAction()]), []);

    // ── Validity ──────────────────────────────────────────────────────────────
    const isValueValid = useCallback((cond: CondEntry): boolean => {
        if (OPERATORS_WITHOUT_VALUE.includes(cond.operator as ConditionOperator)) {
            return true;
        }

        const val = cond.value;
        if (val === undefined || val === null || val === DATETIME_CUSTOM_SENTINEL || (typeof val === 'string' && val.trim() === '')) {
            return false;
        }

        if (Array.isArray(val) && val.length === 0) {
            return false;
        }

        const fType = String(cond.fieldType || '').toLowerCase();

        if (fType === FIELD_TYPES.NUMERIC) {
            return !isNaN(Number(val));
        }

        if (fType === FIELD_TYPES.BOOLEAN) {
            return typeof val === 'boolean' || val === 'true' || val === 'false';
        }

        return true;
    }, []);

    const checkCond = useCallback((c: CondEntry) => {
        const fieldPath = c.fieldPath?.trim() ?? '';
        const hasField = fieldPath.length > 0 && fieldPath !== '__custom__';
        const hasOperator = !!c.operator;
        const hasValue = isValueValid(c);
        return hasField && hasOperator && hasValue;
    }, [isValueValid]);

    const isValid = useMemo(() => {
        if (!isEdit && !ruleSetId) {
            return false;
        }
        if (thenActions.length === 0) {
            return false;
        }

        if (mode === 'simple') {
            return simpleConditions.length > 0 && simpleConditions.every(checkCond);
        }
        // Every group must have at least one child and each child must pass validation.
        const checkChildren = (children: Array<CondEntry | GroupNode>): boolean => {
            if (children.length === 0) {
                return false;
            }
            return children.every((c) => c.kind === 'cond' ? checkCond(c) : checkChildren(c.children));
        };
        return checkChildren(advancedChildren);
    }, [isEdit, ruleSetId, thenActions, mode, simpleConditions, advancedChildren, checkCond]);

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        if (!isValid) {
            return;
        }
        setSaving(true);
        const conditionGroups =
            mode === 'simple'
                ? simpleToConditionGroups(simpleConditions, simpleBehavior)
                : advancedToConditionGroups(advancedChildren);
        const apiActions = actionsToApi(thenActions, elseActions);
        try {
            const editId = freshRule?.id ?? rule?.id;
            if (isEdit && editId) {
                await updateRule(editId, { id: editId, priority, conditionGroups, actions: apiActions });
                addToast({ severity: 'success', body: t(RULE_BUILDER_RULE_UPDATED, 'Rule updated successfully') });
            } else {
                // eslint-disable-next-line camelcase
                await createRule({ rule_set_id: ruleSetId, tid, priority, conditionGroups, actions: apiActions });
                addToast({ severity: 'success', body: t(RULE_BUILDER_RULE_CREATED, 'Rule created successfully') });
            }
            onSaved?.();
            onClose();
        } catch {
            addToast({ severity: 'error', body: DEFAULT_ERROR_MSG });
        } finally {
            setSaving(false);
        }
    }, [isValid, mode, simpleConditions, simpleBehavior, advancedChildren, thenActions, elseActions, isEdit, rule, ruleSetId, tid, priority, addToast, onSaved, onClose, t]);

    const modeTip =
        mode === 'simple'
            ? t(RULE_BUILDER_SIMPLE_TIP, 'Simple mode is perfect for most common rules.')
            : t(RULE_BUILDER_ADVANCED_TIP, 'Advanced mode lets you build complex rules using groups, nested conditions and logic operators.');

    return (
        <Modal show={show} onHide={onClose} fullscreen contentClassName="rb__modal-content" backdrop="static">
            {/* ── Header ─────────────────────────────────────────────────── */}
            <Modal.Header className="rb__header border-0">
                <div className="rb__header-left">
                    <button type="button" className="rb__back-btn" onClick={onClose}>
                        <RiArrowLeftLine size={16} />
                    </button>
                    <div>
                        <Typography variant="h6" className="rb__title mb-0">
                            {isEdit ? t(RULE_BUILDER_EDIT_TITLE, 'Edit Rule') : t(RULE_BUILDER_CREATE_TITLE, 'Create Rule')}
                        </Typography>
                        <Typography variant="caption1" color="text-low-emphasis" className="d-block mb-0 rb__subtitle">
                            {isEdit ? t(RULE_BUILDER_EDIT_SUBTITLE, 'Update your rule configuration.') : t(RULE_BUILDER_CREATE_SUBTITLE, 'Set up when something happens and what action to take.')}
                        </Typography>
                    </div>
                </div>

                <div className="rb__header-right">
                    {/* Rule Set selector */}
                    <div className={`rule-builder-modal__input rb__connector-input ${!ruleSetId ? 'is-invalid' : ''}`} style={{ width: 300, height: 34 }}>
                        <DropdownComponent
                            value={isRuleSetsLoading ? 'Loading…' : (ruleSetId ? selectedRuleSetName : t(RULE_BUILDER_SELECT_RULE_SET, 'Select rule set…'))}
                            dropDownItems={filteredRuleSetItems}
                            onChange={(v) => {
                                setRuleSetId(v ?? '');
                                setRuleSetSearch('');
                            }}
                            variant="filled"
                            isStartCase={false}
                            disableToggle={isEdit}
                            hasSearchBar={!isEdit}
                            searchInput={ruleSetSearch}
                            onSearchChange={(e) => setRuleSetSearch(e.target.value)}
                        />
                    </div>

                    {/* Priority stepper */}
                    <div className="rb__priority-wrap">
                        <button
                            type="button"
                            className="rb__priority-btn"
                            onClick={() => setPriority((p) => Math.max(1, p - 1))}
                            disabled={priority <= 1}
                            aria-label="Decrease priority"
                        >
                            −
                        </button>
                        <span className="rb__priority-value">{priority}</span>
                        <button
                            type="button"
                            className="rb__priority-btn"
                            onClick={() => setPriority((p) => p + 1)}
                            aria-label="Increase priority"
                        >
                            +
                        </button>
                    </div>

                    <Button variant="outline-secondary" size="sm" className="rb__cancel-btn px-4" onClick={onClose} disabled={saving}>
                        {t(RULE_BUILDER_CANCEL, 'Cancel')}
                    </Button>
                    <Button
                        size="sm"
                        disabled={!isValid || saving}
                        onClick={handleSave}
                        className={`rb__save-btn px-4 d-flex align-items-center gap-2 rb__save-btn--${isValid && !saving ? 'active' : 'disabled'}`}
                    >
                        {saving && <span className="rule-builder-modal__spinner" />}
                        {saving ? t(RULE_BUILDER_SAVING, 'Saving…') : t(RULE_BUILDER_SAVE, '✓ Save Rule')}
                    </Button>
                </div>
            </Modal.Header>

            {/* ── Body ───────────────────────────────────────────────────── */}
            <Modal.Body className="p-0 rb__body">
                {ruleLoading && (
                    <div className="rb__fetch-overlay">
                        <div className="rb__fetch-spinner" />
                        <Typography variant="body5" color="text-low-emphasis" className="mb-0 mt-2">
                            {t(RULE_BUILDER_LOADING, 'Loading latest rule data…')}
                        </Typography>
                    </div>
                )}
                {/* ── Left: main form ──────────────────────────────────── */}
                <div className="rb__main custom-scrollbar">
                    {/* Mode tabs + tip */}
                    <div className="rb__mode-row">
                        <div className="d-flex gap-3">
                            <ModeTab
                                active={mode === 'simple'}
                                icon="⊞"
                                title={t(RULE_BUILDER_SIMPLE_MODE_TITLE, 'Simple mode')}
                                subtitle={t(RULE_BUILDER_SIMPLE_MODE_SUBTITLE, 'Quick and easy')}
                                onClick={() => handleSetMode('simple')}
                            />
                            <ModeTab
                                active={mode === 'advanced'}
                                icon="⊟"
                                title={t(RULE_BUILDER_ADVANCED_MODE_TITLE, 'Advanced mode')}
                                subtitle={t(RULE_BUILDER_ADVANCED_MODE_SUBTITLE, 'Use groups and nested conditions')}
                                onClick={() => handleSetMode('advanced')}
                            />
                        </div>
                        <div className="rb__mode-tip">
                            <BsLightbulb size={13} className="rb__mode-tip-icon" />
                            <Typography variant="caption1" color="text-low-emphasis" className="mb-0" style={{ fontSize: 11 }}>
                                {modeTip}
                            </Typography>
                        </div>
                    </div>

                    {/* Mode content */}
                    {mode === 'simple' && (
                        <SimpleMode
                            conditions={simpleConditions}
                            behavior={simpleBehavior}
                            thenActions={thenActions}
                            elseActions={elseActions}
                            onBehaviorChange={setSimpleBehavior}
                            onCondChange={(id, patch) => setSimpleConditions((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)))}
                            onCondRemove={(id) => setSimpleConditions((prev) => prev.length > 1 ? prev.filter((c) => c.id !== id) : prev)}
                            onCondAdd={() => setSimpleConditions((prev) => [...prev, emptyCond()])}
                            onThenChange={(id, patch) => updateActions(setThenActions, id, patch)}
                            onThenRemove={(id) => removeAction(setThenActions, id)}
                            onThenAdd={() => addAction(setThenActions)}
                            onElseChange={(id, patch) => updateActions(setElseActions, id, patch)}
                            onElseRemove={(id) => removeAction(setElseActions, id)}
                            onElseAdd={() => addAction(setElseActions)}
                        />
                    )}

                    {mode === 'advanced' && (
                        <AdvancedMode
                            rootChildren={advancedChildren}
                            thenActions={thenActions}
                            elseActions={elseActions}
                            onRootChange={setAdvancedChildren}
                            onThenChange={(id, patch) => updateActions(setThenActions, id, patch)}
                            onThenRemove={(id) => removeAction(setThenActions, id)}
                            onThenAdd={() => addAction(setThenActions)}
                            onElseChange={(id, patch) => updateActions(setElseActions, id, patch)}
                            onElseRemove={(id) => removeAction(setElseActions, id)}
                            onElseAdd={() => addAction(setElseActions)}
                        />
                    )}
                </div>

                {/* ── Right: sidebar ──────────────────────────────────── */}
                <RuleSidebar
                    mode={mode}
                    simpleBehavior={simpleBehavior}
                    simpleConditions={simpleConditions}
                    advancedChildren={advancedChildren}
                    thenActions={thenActions}
                    elseActions={elseActions}
                    hidden={sidebarHidden}
                    onToggleHide={() => setSidebarHidden((v) => !v)}
                />
            </Modal.Body>
        </Modal>
    );
};

export default RuleBuilderModal;
